// components/SearchForm.js
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchOptions } from '../hooks/useSearchOptions';
import { buildAdvancedSearchFilter, transformFormDataToCriteria } from '../services/searchService';
import './SearchForm.css';

// Collapsible multi-select criterion panel
const CriterionPanel = ({ icon, label, count, options, selected, onToggle, searchable, searchPlaceholder, emptyLabel }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const slug = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '');

  const filteredOptions = useMemo(() => {
    if (!filter) return options;
    const lower = filter.toLowerCase();
    return options.filter(opt => opt.label.toLowerCase().includes(lower));
  }, [options, filter]);

  return (
    <div className={`sf-panel ${isOpen ? 'sf-panel--open' : ''}`}>
      <button
        type="button"
        className="sf-panel__header"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span className="sf-panel__icon">{icon}</span>
        <span className="sf-panel__label">{label}</span>
        {count > 0 && <span className="sf-panel__badge">{count}</span>}
        <span className={`sf-panel__chevron ${isOpen ? 'sf-panel__chevron--open' : ''}`}>▾</span>
      </button>

      {isOpen && (
        <div className="sf-panel__body">
          {searchable && options.length > 6 && (
            <input
              type="text"
              name={`sf-search-${slug(label)}`}
              id={`sf-search-${slug(label)}`}
              className="sf-panel__search"
              placeholder={searchPlaceholder || '🔍'}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
          <div className="sf-panel__list">
            {filteredOptions.length === 0 && (
              <span className="sf-panel__empty">{emptyLabel}</span>
            )}
            {filteredOptions.map((opt) => (
              <label key={opt.value} className="sf-checkbox">
                <input
                  type="checkbox"
                  id={`sf-${slug(label)}-${opt.value}`}
                  name={`sf-${slug(label)}`}
                  value={opt.value}
                  checked={selected.includes(opt.value)}
                  onChange={() => onToggle(opt.value)}
                />
                <span className="sf-checkbox__mark" />
                <span className="sf-checkbox__text">{opt.label}</span>
              </label>
            ))}
          </div>
          {selected.length > 0 && (
            <button
              type="button"
              className="sf-panel__clear"
              onClick={() => selected.forEach(v => onToggle(v))}
            >
              ✕ {t('sf_clearAll')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const SearchForm = ({ onSearch, accessToken, isLoading }) => {
  const { t } = useTranslation();

  // Form selections
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedHealthScores, setSelectedHealthScores] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedProjectTypes, setSelectedProjectTypes] = useState([]);

  const {
    industries,
    healthScoreOptions,
    countries,
    zones,
    partners,
    loadingOptions,
    loadError
  } = useSearchOptions(accessToken, t);

  // Normalize options to { value, label } format
  const industryOptions = useMemo(
    () => industries.map(i => ({ value: i.Value, label: i.Label?.UserLocalizedLabel?.Label || `#${i.Value}` })),
    [industries]
  );
  const healthOptions = useMemo(
    () => healthScoreOptions.map(h => ({ value: h.Value, label: h.Label?.UserLocalizedLabel?.Label || `#${h.Value}` })),
    [healthScoreOptions]
  );
  const countryOpts = useMemo(
    () => countries.map(c => ({ value: c.id, label: c.name })),
    [countries]
  );
  const zoneOpts = useMemo(
    () => zones.map(z => ({ value: z.id, label: z.name })),
    [zones]
  );
  const partnerOpts = useMemo(
    () => partners.map(p => ({ value: p.id, label: p.name })),
    [partners]
  );
  const projectTypeOptions = [
    { value: 'intranet', label: t('mrrIntranet') },
    { value: 'teams', label: t('mrrTeams') },
    { value: 'manager', label: t('mrrManager') }
  ];

  const totalSelected =
    selectedIndustries.length + selectedCountries.length + selectedZones.length + selectedHealthScores.length + selectedPartners.length + selectedProjectTypes.length;

  // Toggle helper
  const makeToggle = (selected, setter) => (value) => {
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  // Submit
  const handleSearch = () => {
    if (totalSelected === 0) return;

    const formData = {
      industries: selectedIndustries,
      countries: selectedCountries,
      zones: selectedZones,
      healthScores: selectedHealthScores,
      partners: selectedPartners,
      projectTypes: selectedProjectTypes,
    };
    const criteria = transformFormDataToCriteria(formData);
    const filter = buildAdvancedSearchFilter(criteria);
    onSearch(filter, formData);
  };

  // Reset
  const handleReset = () => {
    setSelectedIndustries([]);
    setSelectedCountries([]);
    setSelectedZones([]);
    setSelectedHealthScores([]);
    setSelectedPartners([]);
    setSelectedProjectTypes([]);
  };

  return (
    <div className="sf-container">
      {/* Header */}
      <div className="sf-header">
        <span className="sf-header__icon">🔎</span>
        <div>
          <h2 className="sf-header__title">{t('sf_title')}</h2>
          <p className="sf-header__subtitle">{t('sf_subtitle')}</p>
        </div>
      </div>

      {/* Loading / Error states */}
      {loadingOptions && (
        <div className="sf-loading">
          <span className="sf-loading__spinner" />
          <span>{t('sf_loading')}</span>
        </div>
      )}

      {loadError && (
        <div className="sf-error">
          <span>⚠️</span> {loadError}
        </div>
      )}

      {/* Criteria panels */}
      {!loadingOptions && !loadError && (
        <>
          <div className="sf-panels">
            <CriterionPanel
              icon="🏭"
              label={t('industry')}
              count={selectedIndustries.length}
              options={industryOptions}
              selected={selectedIndustries}
              onToggle={makeToggle(selectedIndustries, setSelectedIndustries)}
              searchable
              searchPlaceholder={t('sf_searchPlaceholder')}
              emptyLabel={t('sf_noOptions')}
            />
            <CriterionPanel
              icon="🌍"
              label={t('country')}
              count={selectedCountries.length}
              options={countryOpts}
              selected={selectedCountries}
              onToggle={makeToggle(selectedCountries, setSelectedCountries)}
              searchable
              searchPlaceholder={t('sf_searchPlaceholder')}
              emptyLabel={t('sf_noOptions')}
            />
            <CriterionPanel
              icon="📍"
              label={t('zone')}
              count={selectedZones.length}
              options={zoneOpts}
              selected={selectedZones}
              onToggle={makeToggle(selectedZones, setSelectedZones)}
              searchable
              searchPlaceholder={t('sf_searchPlaceholder')}
              emptyLabel={t('sf_noOptions')}
            />
            <CriterionPanel
              icon="💚"
              label={t('healthScore')}
              count={selectedHealthScores.length}
              options={healthOptions}
              selected={selectedHealthScores}
              onToggle={makeToggle(selectedHealthScores, setSelectedHealthScores)}
              searchable={false}
              emptyLabel={t('sf_noOptions')}
            />
            <CriterionPanel
              icon="🤝"
              label={t('partner')}
              count={selectedPartners.length}
              options={partnerOpts}
              selected={selectedPartners}
              onToggle={makeToggle(selectedPartners, setSelectedPartners)}
              searchable
              searchPlaceholder={t('sf_searchPlaceholder')}
              emptyLabel={t('sf_noOptions')}
            />
            <CriterionPanel
              icon="💻"
              label={t('projectType')}
              count={selectedProjectTypes.length}
              options={projectTypeOptions}
              selected={selectedProjectTypes}
              onToggle={makeToggle(selectedProjectTypes, setSelectedProjectTypes)}
              searchable={false}
              emptyLabel={t('sf_noOptions')}
            />
          </div>

          {/* Selection summary chips */}
          {totalSelected > 0 && (
            <div className="sf-summary">
              <span className="sf-summary__label">{t('sf_activeFilters')}:</span>
              {selectedIndustries.length > 0 && (
                <span className="sf-chip sf-chip--industry">
                  🏭 {selectedIndustries.length} {t('industry')}
                </span>
              )}
              {selectedCountries.length > 0 && (
                <span className="sf-chip sf-chip--country">
                  🌍 {selectedCountries.length} {t('country')}
                </span>
              )}
              {selectedZones.length > 0 && (
                <span className="sf-chip sf-chip--zone">
                  📍 {selectedZones.length} {t('zone')}
                </span>
              )}
              {selectedHealthScores.length > 0 && (
                <span className="sf-chip sf-chip--health">
                  💚 {selectedHealthScores.length} {t('healthScore')}
                </span>
              )}
              {selectedPartners.length > 0 && (
                <span className="sf-chip sf-chip--partner">
                  🤝 {selectedPartners.length} {t('partner')}
                </span>
              )}
              {selectedProjectTypes.length > 0 && (
                <span className="sf-chip sf-chip--project">
                  💻 {selectedProjectTypes.length} {t('projectType')}
                </span>
              )}
            </div>
          )}

          {/* Logic hint */}
          {totalSelected > 0 && (
            <p className="sf-logic-hint">{t('sf_logicHint')}</p>
          )}

          {/* Actions */}
          <div className="sf-actions">
            <button
              className="sf-btn sf-btn--primary"
              onClick={handleSearch}
              disabled={isLoading || totalSelected === 0}
            >
              {isLoading ? (
                <>
                  <span className="sf-btn__spinner" />
                  {t('searching')}
                </>
              ) : (
                <>🔍 {t('sf_search')}</>
              )}
            </button>
            <button
              className="sf-btn sf-btn--ghost"
              onClick={handleReset}
              disabled={isLoading || totalSelected === 0}
            >
              {t('sf_reset')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchForm;
